import React from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";

import {
  Edit,
  Delete,
  Visibility,
  Security,
  AdminPanelSettings,
  GroupWork,
  Campaign,
  Shield,
} from "@mui/icons-material";

import UserCardShimmer from "./UserCardShimmer";

const ROLE_COLORS = {
  1: "bg-purple-500",
  2: "bg-pink-500",
  3: "bg-yellow-600",
  4: "bg-green-500",
  5: "bg-blue-500",
  default: "bg-red-500",
};

const ROLE_ICONS = {
  1: { icon: <Security className="text-yellow-400" />, label: "Super Admin" },
  2: { icon: <AdminPanelSettings className="text-pink-500" />, label: "Admin" },
  3: { icon: <GroupWork className="text-yellow-600" />, label: "Alliance Agent" },
  4: { icon: <Campaign className="text-green-500" />, label: "Campaign Manager" },
  5: { icon: <Shield className="text-blue-500" />, label: "Audit Manager" },
  default: { icon: <Security className="text-red-500" />, label: "User" },
};

const UserCard = ({ users, deleteUser, update, loading, handleShow }) => {
  const { user } = useSelector((state) => state.authReducer);
  const { user_roleid } = user;

  const avatarColor = ROLE_COLORS[users.user_roleid] || ROLE_COLORS.default;
  const roleInfo = ROLE_ICONS[users.user_roleid] || ROLE_ICONS.default;

  if (loading) return <UserCardShimmer />;

  return (
    <motion.div
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="mb-4 w-full"
      style={{ width: "100%" }}
    >
      <div className="w-full user-card-modern shadow-lg rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Avatar */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, ease: "backOut" }}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg ${avatarColor}`}
            >
              {users.user_name[0]}
            </div>
          </motion.div>

          {/* Info */}
          <div className="flex-1 ml-4 overflow-hidden">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-base font-semibold truncate user-name"
            >
              {users.user_name}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-sm truncate user-email"
            >
              {users.user_email}
            </motion.div>

            {/* Creative small role card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-2"
            >
              <div className="flex items-center gap-2 px-3 py-1 rounded-xl shadow-md w-fit role-chip">
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  className="text-base"
                >
                  {roleInfo.icon}
                </motion.div>
                <span className="text-xs font-medium whitespace-nowrap">
                  {roleInfo.label}
                </span>
              </div>
            </motion.div>
          </div>

          {/* Action Buttons */}
          {user_roleid === 1 && (
            <div className="flex items-center gap-2 ml-2 user-actions">
              <motion.button
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => update(users)}
                className="text-green-500 hover:text-green-600 transition"
              >
                <Edit fontSize="small" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleShow(users)}
                className="text-gray-900 hover:text-green-900 transition mt-1"
              >
                <Visibility fontSize="small" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => deleteUser(users.user_id)}
                className="text-red-500 hover:text-red-600 transition"
              >
                <Delete fontSize="small" />
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default UserCard;
